import {
  isCurrentAccountScopedStorageKey,
  listAccountScopedStorage,
  removeAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  readIsolatedRows,
  removeIsolatedRow,
  writeIsolatedRow,
  type IsolatedRowIo,
} from "@/shared/storage/isolated-row-state.mjs";
import {
  cancelSavedArticleDeletion,
  queueSavedArticleDeletion,
  type SyncSavedArticleDeletion,
} from "@/shared/sync/deletion-outbox";
import { journalCurrentContentPushIntentSync } from "@/shared/sync/content-push-intent";
import {
  authoritativeRowIdsToPrune,
  syncRowsStructurallyEqual,
} from "@/shared/sync/authoritative-row-prune.mjs";
import { remoteTombstoneDeletesLocal } from "@/shared/sync/remote-tombstone-policy.mjs";
import {
  nextAccountRowMutationIso,
  ROW_MUTATION_DOMAINS,
} from "@/shared/sync/account-row-mutation-clock";

const SAVED_ARTICLE_ROW_PREFIX = "tb_public_web_saved_article_v2:";
const SAVED_EVENT = "tb-public-web-saved-changed";

export type SavedArticle = {
  /** Android/server sync identity. This may be `title:…` when no link exists. */
  articleId: string;
  url: string;
  title: string;
  source?: string;
  categoryLabel?: string;
  description?: string;
  contentLicense?: string;
  usageRights?: string;
  savedAtEpochMs: number;
  updatedAt: string;
};

export type SyncSavedArticle = {
  articleId?: string;
  title?: string;
  link?: string | null;
  source?: string | null;
  description?: string | null;
  contentLicense?: string | null;
  usageRights?: string | null;
  categoryLabel?: string | null;
  savedAtEpochMs?: number;
  updatedAt?: string;
};

export type ApplySyncSavedArticlesOptions = {
  merge?: boolean;
  authoritativeFullPull?: boolean;
  authoritativeBaseline?: SavedArticle[];
  preferIncomingWhenLocalUnchanged?: boolean;
  preserveExistingLocalRows?: boolean;
};

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function savedArticleRowSlot(articleId: string): string {
  return encodeURIComponent(articleId);
}

const savedArticleRowIo: IsolatedRowIo = {
  list() {
    return listAccountScopedStorage(SAVED_ARTICLE_ROW_PREFIX).map(
      ({ baseKey, value }) => ({
        slot: baseKey.slice(SAVED_ARTICLE_ROW_PREFIX.length),
        value,
      }),
    );
  },
  write(slot, value) {
    writeAccountScopedStorage(`${SAVED_ARTICLE_ROW_PREFIX}${slot}`, value);
  },
  remove(slot) {
    removeAccountScopedStorage(`${SAVED_ARTICLE_ROW_PREFIX}${slot}`);
  },
};

function normalizeUrl(url: string | undefined | null): string {
  return String(url || "").trim();
}

function normalizeArticle(article: Partial<SavedArticle>): SavedArticle | null {
  const title =
    String(article.title || "Article")
      .trim()
      .slice(0, 500) || "Article";
  const rawUrl = normalizeUrl(article.url);
  const rawArticleId = String(article.articleId || "").trim();
  const articleId = rawArticleId || rawUrl || `title:${title}`;
  if (!articleId) return null;
  // Migrate the old web representation that placed Android's title-only
  // identity in the URL field. Reserved sync identities are not links.
  const url = rawUrl === articleId && /^(?:title:|id:)/.test(rawUrl) ? "" : rawUrl;
  return {
    articleId,
    url,
    title,
    source: String(article.source || "").slice(0, 120),
    categoryLabel: String(article.categoryLabel || "").slice(0, 240),
    description: String(article.description || "").slice(0, 8_000),
    contentLicense: String(article.contentLicense || "")
      .trim()
      .slice(0, 1_000),
    usageRights: String(article.usageRights || "")
      .trim()
      .slice(0, 1_000),
    savedAtEpochMs: Number(article.savedAtEpochMs) || Date.now(),
    updatedAt: typeof article.updatedAt === "string" ? article.updatedAt : "",
  };
}

export function loadSavedArticles(): SavedArticle[] {
  const store = storage();
  if (!store) return [];
  try {
    return readIsolatedRows(savedArticleRowIo)
      .map(({ slot, value }) => {
        const article = normalizeArticle(value as Partial<SavedArticle>);
        return article && savedArticleRowSlot(article.articleId) === slot
          ? article
          : null;
      })
      .filter((item): item is SavedArticle => Boolean(item))
      .sort((a, b) => b.savedAtEpochMs - a.savedAtEpochMs);
  } catch {
    return [];
  }
}

function emitSavedArticlesChanged(): SavedArticle[] {
  const articles = loadSavedArticles();
  window.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { articles } }));
  return articles;
}

function persistSavedArticleRow(
  article: SavedArticle,
  journalSyncIntent = true,
): void {
  if (journalSyncIntent) {
    journalCurrentContentPushIntentSync({ includeSavedArticles: true });
  }
  writeIsolatedRow(
    savedArticleRowIo,
    savedArticleRowSlot(article.articleId),
    article,
  );
}

export function saveSavedArticles(
  articles: SavedArticle[],
  options: { journalSyncIntent?: boolean } = {},
): SavedArticle[] {
  const deduped = new Map<string, SavedArticle>();
  articles.forEach((article) => {
    const normalized = normalizeArticle(article);
    if (normalized) deduped.set(normalized.articleId, normalized);
  });
  const next = [...deduped.values()].sort((a, b) => b.savedAtEpochMs - a.savedAtEpochMs);
  const store = storage();
  if (store) {
    try {
      if (options.journalSyncIntent !== false) {
        journalCurrentContentPushIntentSync({ includeSavedArticles: true });
      }
      next.forEach((article) =>
        writeIsolatedRow(
          savedArticleRowIo,
          savedArticleRowSlot(article.articleId),
          article,
        ),
      );
    } catch {
      /* saved article writes are best effort */
    }
  }
  return emitSavedArticlesChanged();
}

export function saveArticle(input: {
  url: string;
  title?: string;
  source?: string;
  categoryLabel?: string;
  description?: string;
  contentLicense?: string;
  usageRights?: string;
}): SavedArticle[] {
  const current = loadSavedArticles();
  const url = normalizeUrl(input.url);
  if (!url) return current;
  cancelSavedArticleDeletion(url);
  const existing = current.find((item) => item.url === url);
  const article = normalizeArticle({
    articleId: url,
    url,
    title: input.title || existing?.title || "Article",
    source: input.source || existing?.source || "",
    categoryLabel: input.categoryLabel || existing?.categoryLabel || "",
    description: input.description || existing?.description || "",
    contentLicense: input.contentLicense || existing?.contentLicense || "",
    usageRights: input.usageRights || existing?.usageRights || "",
    savedAtEpochMs: existing?.savedAtEpochMs || Date.now(),
    updatedAt: nextAccountRowMutationIso(
      ROW_MUTATION_DOMAINS.savedArticles,
      existing?.articleId || url,
      existing?.updatedAt,
    ),
  });
  if (!article) return current;
  persistSavedArticleRow(article);
  return emitSavedArticlesChanged();
}

export function removeSavedArticle(url: string): {
  next: SavedArticle[];
  removed: SavedArticle | null;
} {
  const current = loadSavedArticles();
  const identifier = normalizeUrl(url);
  const removed =
    current.find((item) => item.articleId === identifier || item.url === identifier) || null;
  if (!removed) return { next: current, removed };
  queueSavedArticleDeletion(removed.articleId, removed.updatedAt);
  journalCurrentContentPushIntentSync({ includeSavedArticles: true });
  removeIsolatedRow(savedArticleRowIo, savedArticleRowSlot(removed.articleId));
  return { next: emitSavedArticlesChanged(), removed };
}

export function restoreSavedArticle(article: SavedArticle): SavedArticle[] {
  const current = loadSavedArticles();
  cancelSavedArticleDeletion(article.articleId);
  if (current.some((item) => item.articleId === article.articleId)) return current;
  persistSavedArticleRow({
    ...article,
    updatedAt: nextAccountRowMutationIso(
      ROW_MUTATION_DOMAINS.savedArticles,
      article.articleId,
      article.updatedAt,
    ),
  });
  return emitSavedArticlesChanged();
}

export function isArticleSaved(url: string, articles = loadSavedArticles()): boolean {
  const normalizedUrl = normalizeUrl(url);
  return Boolean(normalizedUrl && articles.some((article) => article.url === normalizedUrl));
}

export function savedArticlesToSyncPayload(articles = loadSavedArticles()): SyncSavedArticle[] {
  return articles.map((article) => ({
    articleId: article.articleId,
    title: article.title,
    link: article.url || null,
    source: article.source || null,
    description: article.description || null,
    contentLicense: article.contentLicense || null,
    usageRights: article.usageRights || null,
    categoryLabel: article.categoryLabel || null,
    savedAtEpochMs: article.savedAtEpochMs || Date.now(),
    updatedAt: article.updatedAt || undefined,
  }));
}

function remoteArticleIsNewer(remote: SavedArticle, current: SavedArticle | undefined): boolean {
  if (!current) return true;
  const remoteMs = Date.parse(remote.updatedAt || "");
  const currentMs = Date.parse(current.updatedAt || "");
  if (!Number.isFinite(remoteMs)) return true;
  if (!Number.isFinite(currentMs)) return true;
  return remoteMs >= currentMs;
}

function mergeSavedArticles(
  current: SavedArticle[],
  incoming: SavedArticle[],
  options: Pick<
    ApplySyncSavedArticlesOptions,
    | "authoritativeBaseline"
    | "preferIncomingWhenLocalUnchanged"
    | "preserveExistingLocalRows"
  > = {},
): SavedArticle[] {
  const byId = new Map(current.map((article) => [article.articleId, article]));
  const hasAuthoritativeBaseline = Array.isArray(options.authoritativeBaseline);
  const baselineById = new Map(
    (options.authoritativeBaseline || []).map((article) => [article.articleId, article]),
  );
  incoming.forEach((article) => {
    const existing = byId.get(article.articleId);
    if (existing && options.preserveExistingLocalRows === true) return;
    if (options.preferIncomingWhenLocalUnchanged === true && hasAuthoritativeBaseline) {
      const baseline = baselineById.get(article.articleId);
      if (baseline && existing && syncRowsStructurallyEqual(baseline, existing)) {
        byId.set(article.articleId, article);
      } else if (!baseline && !existing) {
        byId.set(article.articleId, article);
      }
      return;
    }
    if (remoteArticleIsNewer(article, existing)) byId.set(article.articleId, article);
  });
  return [...byId.values()];
}

export function applySyncSavedArticles(
  articles: SyncSavedArticle[] | null | undefined,
  options: ApplySyncSavedArticlesOptions = {},
): SavedArticle[] {
  if (!Array.isArray(articles)) return loadSavedArticles();
  const next = articles
    .map((article) =>
      normalizeArticle({
        articleId: article.articleId || article.link || "",
        url: article.link || "",
        title: article.title || "Article",
        source: article.source || "",
        categoryLabel: article.categoryLabel || "",
        description: article.description || "",
        contentLicense: article.contentLicense || "",
        usageRights: article.usageRights || "",
        savedAtEpochMs: article.savedAtEpochMs || Date.now(),
        updatedAt: article.updatedAt || new Date().toISOString(),
      }),
    )
    .filter((item): item is SavedArticle => Boolean(item));
  const current = loadSavedArticles();
  const resolved = options.merge
    ? mergeSavedArticles(current, next, options)
    : next;
  const currentById = new Map(
    current.map((article) => [article.articleId, article]),
  );
  let changed = false;
  for (const article of resolved) {
    if (
      syncRowsStructurallyEqual(currentById.get(article.articleId), article)
    ) {
      continue;
    }
    persistSavedArticleRow(article, false);
    changed = true;
  }
  if (options.authoritativeFullPull) {
    for (const articleId of authoritativeRowIdsToPrune(
      options.authoritativeBaseline || [],
      current,
      next,
      "articleId",
    )) {
      removeIsolatedRow(
        savedArticleRowIo,
        savedArticleRowSlot(articleId),
      );
      changed = true;
    }
  }
  return changed ? emitSavedArticlesChanged() : current;
}

export function applySyncSavedArticleDeletions(
  deletions: Array<Partial<SyncSavedArticleDeletion>> | null | undefined,
  options: {
    respectLocalUpdatedAt?: boolean;
    authoritativeBaseline?: SavedArticle[];
    preferDeletionWhenLocalUnchanged?: boolean;
    preserveExistingLocalRows?: boolean;
  } = {},
): SavedArticle[] {
  if (!Array.isArray(deletions) || deletions.length === 0) return loadSavedArticles();
  const byId = new Map(
    deletions
      .map((item) => [normalizeUrl(item.articleId), item] as const)
      .filter(([id]) => Boolean(id)),
  );
  const current = loadSavedArticles();
  const hasAuthoritativeBaseline = Array.isArray(options.authoritativeBaseline);
  const baselineById = new Map(
    (options.authoritativeBaseline || []).map((article) => [article.articleId, article]),
  );
  let changed = false;
  for (const article of current) {
    const deletion = byId.get(article.articleId);
    if (!deletion) continue;
    if (options.preserveExistingLocalRows === true) continue;
    if (options.preferDeletionWhenLocalUnchanged === true && hasAuthoritativeBaseline) {
      const baseline = baselineById.get(article.articleId);
      if (!baseline || !syncRowsStructurallyEqual(baseline, article)) continue;
      removeIsolatedRow(
        savedArticleRowIo,
        savedArticleRowSlot(article.articleId),
      );
      changed = true;
      continue;
    }
    const keep =
      options.respectLocalUpdatedAt === true &&
      !remoteTombstoneDeletesLocal(deletion.deletedAt, article.updatedAt);
    if (keep) continue;
    removeIsolatedRow(
      savedArticleRowIo,
      savedArticleRowSlot(article.articleId),
    );
    changed = true;
  }
  return changed ? emitSavedArticlesChanged() : current;
}

export function onSavedArticlesChanged(listener: () => void): () => void {
  window.addEventListener(SAVED_EVENT, listener);
  const onStorage = (event: StorageEvent) => {
    if (
      isCurrentAccountScopedStorageKey(
        event.key,
        SAVED_ARTICLE_ROW_PREFIX,
      )
    ) {
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(SAVED_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
